"use client"

import { useRouter } from "next/navigation"
import { useRef, useState, type FormEvent } from "react"
import { Search } from "lucide-react"
import { useTranslations } from "next-intl"
import { trackMeta } from "@/lib/meta-pixel"
import { Input } from "@/components/ui/input"
import { useRotator } from "@/hooks/use-rotator"
import { cn } from "@/lib/utils"

// Same bar as /search, so the placeholder turns over on the same beat there.
// 5.2s read as slow — a visitor had already stopped watching before the
// second phrase arrived. 3.6s keeps the rotation visibly alive without
// turning the field into a ticker.
const BEAT_MS = 3600

/**
 * The hero's search field.
 *
 * The landing page explains what BOOTH is, which is the right job for someone
 * who has never heard of it. Someone who already knows wants to type a city
 * and go, and making them read a pitch first to reach a button is a tax on the
 * visitor closest to converting.
 *
 * Barber audience only. /search is work search — chairs and job listings — so
 * it is not where an owner or a client is heading, and a search box that
 * searches the wrong thing is worse than none.
 *
 * Styled like the pill on /search: same Input, fill, hairline, centred text,
 * rotating placeholder and bare glyph on the right. Landing on a page that
 * looks like the box you typed into is most of what makes a search feel
 * connected. Same .search-pill height too (36px, the same as the compact
 * button under it). If one changes, change the other.
 */
export function HeroSearch() {
  const t = useTranslations("marketing.landing.heroSearch")
  const router = useRouter()
  const [value, setValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const placeholders = t.raw("placeholders") as string[]
  const placeholder = useRotator(placeholders.length, BEAT_MS, BEAT_MS / 2)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const q = value.trim()
    // A city, state or shop name. Never anything that identifies the visitor.
    trackMeta("Search", { search_string: q || undefined, content_category: "shops" })
    // An empty box still means "take me to search" — it just arrives unfiltered
    // rather than with ?q= on the URL for no reason.
    router.push(q ? `/search?q=${encodeURIComponent(q)}` : "/search")
  }

  return (
    <form onSubmit={submit} role="search" className="mx-auto mt-8 w-[88%] max-w-[538px] md:w-4/5">
      <div className="relative w-full rounded-full">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={t("label")}
          // The visible placeholder is the rotating span below, as on /search.
          placeholder=""
          enterKeyHint="search"
          autoComplete="off"
          className="search-pill rounded-full border-0 px-11 text-center text-sm text-white backdrop-blur-xl placeholder:text-neutral-500 focus-visible:ring-0 focus-visible:ring-offset-0 md:text-base"
          style={{
            background: "rgba(8,8,8,0.72)",
            boxShadow: "inset 0 0 0 0.75px #323232, inset 0 1px 0 rgba(255,255,255,0.05)",
            // Same clip as /search: keeps the blur from repainting square on hover.
            clipPath: "inset(0 round 9999px)",
          }}
        />
        {!value && (
          <span
            key={placeholder.index}
            aria-hidden
            className={cn(
              placeholder.exiting ? "search-text-out search-text-out--soft" : "search-text-in search-text-in--soft",
              "pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-neutral-500 md:text-base",
            )}
          >
            {placeholders[placeholder.index]}
          </span>
        )}
        <button
          type="submit"
          aria-label={t("submit")}
          className="absolute right-3 top-1/2 grid size-7 -translate-y-1/2 place-items-center text-white/80 transition-colors hover:text-white active:scale-95"
        >
          <Search className="size-4" strokeWidth={1.75} />
        </button>
      </div>
    </form>
  )
}
